import React, { useState, useEffect } from "react";
import {
  Container,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  Typography,
  Box,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  AlertTitle,
  Stack,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  LinearProgress,
  Chip,
  Avatar,
} from "@mui/material";
import {
  CloudUpload,
  Description,
  Person,
  CheckCircle,
  Delete,
  UploadFile,
  Info,
  Warning,
  Article,
  Draw,
  Group,
} from "@mui/icons-material";
import * as myApi from "../../components/Api";
import { toast } from "react-toastify";
import { showLoading } from "react-global-loading";
import ConfirmationDialog from "../../components/UI/ConfirmationDialog";
import {
  validateFile,
  validatePaperTitle,
  validateAuthorName,
  validatePhone,
} from "../../utils/formValidation";

/**
 * Modern Create Paper Page with Material UI
 * 4-Step Wizard:
 * 1. Paper Information
 * 2. Author Details
 * 3. File Uploads
 * 4. Review & Save
 */
export default function CreatePaper() {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [deadlineInfo, setDeadlineInfo] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loadingUserData, setLoadingUserData] = useState(true);
  const [hasActiveSubmission, setHasActiveSubmission] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Step 1: Paper Info
  const [paperType, setPaperType] = useState("General Paper");
  const [subTheme, setSubTheme] = useState("");
  const [category, setCategory] = useState("");
  const [participationType, setParticipationType] = useState("Individu");
  const [title, setTitle] = useState("");
  const [titleError, setTitleError] = useState("");

  // Step 2: Authors
  const [authors, setAuthors] = useState([
    { name: "", email: "", phone: "", organization: "", address: "" },
  ]);

  // Step 3: Files
  const [files, setFiles] = useState({
    paper_file: null,
    cv_files: [],
    statement_file: null,
    pddikti_files: [],
    anonymous_manuscript: null,  // Manuskrip Paper (Anonymous/Blind Version)
    ktp_files: [],               // KTP for each author (only for Umum category)
  });
  const [fileErrors, setFileErrors] = useState({});
  
  // Remove author confirmation
  const [removeAuthorDialog, setRemoveAuthorDialog] = useState({ open: false, index: null, name: "" });

  // Review step confirmation checkbox
const [confirmAccuracy, setConfirmAccuracy] = useState(false);
  const [usedSubThemes, setUsedSubThemes] = useState([]);
  const [subThemes, setSubThemes] = useState([]);

  const defaultSubThemes = [
    { id: 1, name: "Peningkatan Daya Saing Industri, Hilirisasi, dan Investasi" },
    { id: 2, name: "Ketahanan Pangan Terintegrasi dan Stabilitas Harga" },
    { id: 3, name: "Perdagang, Konektivitas, dan Integrasi Ekonomi Regional" },
    { id: 4, name: "Pariwiata dan Sumber Ekonomi Baru" },
    { id: 5, name: "Pengembangan UMKM, Inklusi Keuangan, dan Inovasi Pembiayaan" },
    { id: 6, name: "Perbaikan Kualitas Pembelajaran dan Peningkatan Lulusan Pendidikan Tinggi" },
  ];

  const [subThemesLoaded, setSubThemesLoaded] = useState(false);

  useEffect(() => {
    const fetchSubThemes = async () => {
      try {
        const response = await myApi.get("/sub-themes/");
        console.log("[Create] Sub-themes response:", response.data);
        if (response.data && (response.data.result || response.data.data)) {
          setSubThemes(response.data.result || response.data.data);
        } else {
          setSubThemes(defaultSubThemes);
        }
      } catch (error) {
        console.error("Error fetching sub-themes, using defaults:", error);
        setSubThemes(defaultSubThemes);
      }
      setSubThemesLoaded(true);
    };
    fetchSubThemes();
  }, []);

  useEffect(() => {
    console.log("[Create] useEffect triggered, subThemesLoaded:", subThemesLoaded);
    if (!subThemesLoaded) return;
    checkDeadline();
    loadUserData();
    checkUserSubmission();
  }, [subThemesLoaded]);

  // Additional check - also run when userData is loaded
  useEffect(() => {
    if (userData) {
      checkUserSubmission();
    }
  }, [userData]);

  const checkUserSubmission = async () => {
    try {
      const response = await myApi.get("/user/submission-status");
      console.log("[Create] submission status response:", response.data);
      
      if (response.data && response.data.status === 'success') {
        const hasSubmittedPaper = response.data.hasSubmittedPaper;
        console.log("[Create] hasSubmittedPaper:", hasSubmittedPaper);
        setHasActiveSubmission(hasSubmittedPaper);
      }
      setInitialized(true);
    } catch (error) {
      console.error("Error checking user submission:", error);
      setInitialized(true);
    }
  };

  const checkDeadline = async () => {
    try {
      const response = await myApi.get("/deadline");
      if (response.data.result) {
        const deadlineInfo = response.data.result;
        const dateNow = new Date();
        const dateLimit = new Date(deadlineInfo.general_deadline);

        if (dateNow > dateLimit) {
          toast("Submission is closed", { type: "error" });
          setTimeout(() => {
            window.location.href = "/paper/list/";
          }, 3000);
        } else {
          setDeadlineInfo(deadlineInfo);
        }
      }
    } catch (error) {
      console.error("Error fetching deadline:", error);
    }
  };

  const loadUserData = async () => {
    try {
      setLoadingUserData(true);
      console.log('Fetching user profile from /user/profile/...');
      const response = await myApi.get("/user/profile/");
      console.log('Profile API Response:', response.data);
      
      if (response.data && response.data.data) {
        const userData = response.data.data;
        console.log('User data loaded:', userData);
        setUserData(userData);
        setAuthors([
          {
            name: userData.name || "",
            email: userData.email || "",
            phone: userData.phone || "",
            organization: userData.organization || "",
            address: "",
          },
        ]);
      } else {
        console.error('No user data in response. Response:', response.data);
        toast("Failed to load user data. Please refresh the page.", { type: "error" });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config
      });
      const errorMsg = error.response?.data?.message || error.message || "Failed to load user profile";
      toast(errorMsg, { type: "error" });
    } finally {
      setLoadingUserData(false);
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const validateStep = (step) => {
    if (step === 0) {
      // Validate paper info
      const titleValidation = validatePaperTitle(title);
      setTitleError(titleValidation.valid ? "" : titleValidation.message);
      if (!titleValidation.valid) {
        toast(titleValidation.message, { type: "error" });
        return false;
      }
      if (!subTheme) {
        toast("Please select a sub-theme", { type: "error" });
        return false;
      }
      if (!category) {
        toast("Please select a category", { type: "error" });
        return false;
      }
      if (!participationType) {
        toast("Please select participation type", { type: "error" });
        return false;
      }
    } else if (step === 1) {
      // Validate authors
      for (let i = 0; i < authors.length; i++) {
        const author = authors[i];
        if (!author.name || !author.email || !author.phone || !author.organization) {
          toast(`Please fill all fields for Author ${i + 1}`, { type: "error" });
          return false;
        }
        const phoneValidation = validatePhone(author.phone);
        if (!phoneValidation.valid) {
          toast(phoneValidation.message, { type: "error" });
          return false;
        }
      }
      
      // Validate minimum authors for Group participation
      if (participationType === "Group" && authors.length < 2) {
        toast("Group participation requires at least 2 authors. Please add another author.", { 
          type: "warning",
          autoClose: 5000,
        });
        return false;
      }
    } else if (step === 2) {
      // Validate files
      if (!files.paper_file) {
        toast("Please upload paper file", { type: "error" });
        return false;
      }
      if (!files.statement_file) {
        toast("Please upload statement letter", { type: "error" });
        return false;
      }
      if (!files.anonymous_manuscript) {
        toast("Please upload anonymous manuscript (blind version)", { type: "error" });
        return false;
      }
      // Validate KTP for each author when category is Umum
      if (category === "Umum") {
        for (let i = 0; i < authors.length; i++) {
          if (!files.ktp_files[i]) {
            toast(`Please upload KTP for Author ${i + 1}`, { type: "error" });
            return false;
          }
        }
      }
      for (let i = 0; i < authors.length; i++) {
        if (!files.cv_files[i]) {
          toast(`Please upload CV for Author ${i + 1}`, { type: "error" });
          return false;
        }
        if (category === "Mahasiswa" && !files.pddikti_files[i]) {
          toast(`Please upload PDDIKTI proof for Author ${i + 1}`, { type: "error" });
          return false;
        }
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateStep(2)) return;

    // Check if userData is loaded
    if (!userData || !userData.userid_code) {
      toast("User data not loaded. Please refresh the page and try again.", { type: "error" });
      setLoading(false);
      return;
    }

    setLoading(true);
    showLoading(true);

    const formData = new FormData();
    formData.append("jenis_paper_text", paperType);
    formData.append("sub_tema_text", subTheme);
    formData.append("kategori", category);
    formData.append("keikutsertaan", participationType);
    formData.append("judul", title);

    // Add author data - use field names that backend expects (nama, instansi for group papers)
    authors.forEach((author, index) => {
      formData.append(`name_${index + 1}`, author.name);
      formData.append(`nama_${index + 1}`, author.name);  // Backend expects nama_2, nama_3
      formData.append(`email_${index + 1}`, author.email);
      formData.append(`phone_${index + 1}`, author.phone);
      formData.append(`organization_${index + 1}`, author.organization);
      formData.append(`instansi_${index + 1}`, author.organization);  // Backend expects instansi_2, instansi_3
      formData.append(`address_${index + 1}`, author.address || "");  // Backend expects address_2, address_3
    });

    // Add files - match backend field names
    formData.append("paper_file", files.paper_file);
    formData.append("pernyataan_file", files.statement_file);
    files.cv_files.forEach((cv, index) => {
      // Backend expects: cv_file, cv_2_file, cv_3_file
      const fieldName = index === 0 ? "cv_file" : `cv_${index + 1}_file`;
      formData.append(fieldName, cv);
    });
    files.pddikti_files.forEach((pddikti, index) => {
      // Backend expects: pddikti_file, pddikti_2_file, pddikti_3_file
      const fieldName = index === 0 ? "pddikti_file" : `pddikti_${index + 1}_file`;
      formData.append(fieldName, pddikti);
    });
    
    // Add anonymous manuscript file
    if (files.anonymous_manuscript) {
      formData.append("anonymous_manuscript", files.anonymous_manuscript);
    }
    
    // Add KTP files for each author (only for Umum category)
    if (category === "Umum") {
      files.ktp_files.forEach((ktp, index) => {
        if (ktp) {
          formData.append(`ktp_file_${index + 1}`, ktp);
        }
      });
    }

    try {
      const params = new URLSearchParams();
      params.append("userid_code", userData.userid_code);
      params.append("name", userData.name);
      params.append("email", userData.email);
      params.append("phone", userData.phone);
      params.append("organization", userData.organization);

      const endpoint = participationType === "Group" ? "/savePaperGroup" : "/savePaperOne";
      const response = await myApi.post(`${endpoint}?${params.toString()}`, formData, "multipart/form-data");

      showLoading(false);
      toast("Paper submitted successfully!", { type: "success" });
      setTimeout(() => {
        window.location.href = "/paper/list/";
      }, 2000);
    } catch (error) {
      showLoading(false);
      toast(error.message || "Error submitting paper", { type: "error" });
      setLoading(false);
    }
  };

  const handleFileUpload = (event, fileType, index = null) => {
    const file = event.target.files[0];
    if (!file) return;

    const validation = validateFile(file);
    if (!validation.valid) {
      setFileErrors((prev) => ({ ...prev, [fileType]: validation.message }));
      return;
    }

    setFileErrors((prev) => ({ ...prev, [fileType]: "" }));
    if (index !== null) {
      // Handle array-based files (cv_files, pddikti_files, ktp_files)
      let currentFiles;
      if (fileType === "cv_files") {
        currentFiles = files.cv_files;
      } else if (fileType === "pddikti_files") {
        currentFiles = files.pddikti_files;
      } else if (fileType === "ktp_files") {
        currentFiles = files.ktp_files;
      } else {
        currentFiles = [];
      }
      const newFiles = [...currentFiles];
      newFiles[index] = file;
      setFiles((prev) => ({
        ...prev,
        [fileType]: newFiles,
      }));
    } else {
      setFiles((prev) => ({ ...prev, [fileType]: file }));
    }
  };

  const steps = ["Paper Information", "Author Details", "File Uploads", "Review & Save"];

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPaperInfo();
      case 1:
        return renderAuthorDetails();
      case 2:
        return renderFileUploads();
      case 3:
        return renderReview();
      default:
        return null;
    }
  };

  const renderPaperInfo = () => (
    <Paper sx={{ p: 4, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Paper Information
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box>
          <FormControl component="fieldset">
            <FormLabel component="legend">Paper Type</FormLabel>
            <RadioGroup
              row
              value={paperType}
              onChange={(e) => setPaperType(e.target.value)}
            >
              <FormControlLabel
                value="General Paper"
                control={<Radio />}
                label="General Paper"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box>
          <FormControl fullWidth>
            <FormLabel>Sub-Theme</FormLabel>
            <select
              value={subTheme}
              onChange={(e) => setSubTheme(e.target.value)}
              style={{
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid rgba(0,0,0,0.23)",
                width: "100%",
                marginTop: "8px",
                backgroundColor: "#ffffff",
                color: "#1a1a1a",
                fontSize: "1rem",
              }}
            >
              <option value="">Select Sub-Theme</option>
              {(subThemes.length > 0 ? subThemes : defaultSubThemes).map((theme) => {
                const isUsed = usedSubThemes.includes(theme.name);
                return (
                  <option key={theme.id} value={theme.name} disabled={isUsed}>
                    {isUsed ? `${theme.name} (Already Submitted)` : theme.name}
                  </option>
                );
              })}
            </select>
          </FormControl>
        </Box>

        <Box>
          <FormControl component="fieldset">
            <FormLabel component="legend">Category</FormLabel>
            <RadioGroup
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <FormControlLabel
                value="Umum"
                control={<Radio />}
                label="Umum (General)"
              />
              <FormControlLabel
                value="Mahasiswa"
                control={<Radio />}
                label="Mahasiswa (Student)"
              />
            </RadioGroup>
          </FormControl>
        </Box>

        <Box>
          <FormControl component="fieldset">
            <FormLabel component="legend">Participation Type</FormLabel>
            <RadioGroup
              value={participationType}
              onChange={(e) => setParticipationType(e.target.value)}
            >
              <FormControlLabel
                value="Individu"
                control={<Radio />}
                label="Individual"
              />
              <FormControlLabel
                value="Group"
                control={<Radio />}
                label="Group (2-3 Authors)"
              />
            </RadioGroup>
            {participationType === "Group" && (
              <Alert severity="info" sx={{ mt: 2 }} icon={<Info />}>
                <AlertTitle>Group Requirement</AlertTitle>
                Group participation requires <strong>at least 2 authors</strong>. You can add up to 3 authors total.
              </Alert>
            )}
          </FormControl>
        </Box>

        <Box>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Paper Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            error={!!titleError}
            helperText={titleError || `${title.length}/200 characters`}
            placeholder="Enter your paper title (minimum 10 characters)"
          />
        </Box>

        {deadlineInfo && (
          <Box>
            <Alert severity="info" icon={<Info />}>
              <AlertTitle>Deadline Information</AlertTitle>
              Submission deadline: 11 September 2026 WITA
            </Alert>
          </Box>
        )}
      </Box>
    </Paper>
  );

  const renderAuthorDetails = () => (
    <Paper sx={{ p: 4, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Author Details
      </Typography>
      <Divider sx={{ mb: 3 }} />

      {participationType === "Group" && authors.length < 2 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
          <AlertTitle>Minimum Authors Required</AlertTitle>
          Group participation requires at least <strong>2 authors</strong>. You currently have {authors.length}. 
          Please use the "Add Another Author" button below.
        </Alert>
      )}

      {authors.map((author, index) => (
        <Box key={index} sx={{ mb: 4 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Author {index + 1} {index === 0 ? "(Primary)" : ""}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={author.name}
              disabled={index === 0}
              onChange={(e) => {
                const newAuthors = [...authors];
                newAuthors[index].name = e.target.value;
                setAuthors(newAuthors);
              }}
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={author.email}
              disabled={index === 0}
              onChange={(e) => {
                const newAuthors = [...authors];
                newAuthors[index].email = e.target.value;
                setAuthors(newAuthors);
              }}
              required
            />
            <TextField
              fullWidth
              label="Phone"
              value={author.phone}
              disabled={index === 0}
              onChange={(e) => {
                const newAuthors = [...authors];
                newAuthors[index].phone = e.target.value;
                setAuthors(newAuthors);
              }}
              required
            />
            <TextField
              fullWidth
              label="Organization"
              value={author.organization}
              disabled={index === 0}
              onChange={(e) => {
                const newAuthors = [...authors];
                newAuthors[index].organization = e.target.value;
                setAuthors(newAuthors);
              }}
              required
            />
            <TextField
              fullWidth
              label="Address"
              multiline
              rows={2}
              value={author.address}
              disabled={index === 0}
              onChange={(e) => {
                const newAuthors = [...authors];
                newAuthors[index].address = e.target.value;
                setAuthors(newAuthors);
              }}
            />
          </Box>
          {index > 0 && (
            <Button
              variant="text"
              color="error"
              startIcon={<Delete />}
              onClick={() => 
                setRemoveAuthorDialog({ 
                  open: true, 
                  index, 
                  name: author.name || `Author ${index + 1}` 
                })
              }
              sx={{ mt: 2 }}
            >
              Remove Author
            </Button>
          )}
        </Box>
      ))}

      {participationType === "Group" && authors.length < 3 && (
        <Button
          variant="outlined"
          onClick={() =>
            setAuthors([
              ...authors,
              { name: "", email: "", phone: "", organization: "", address: "" },
            ])
          }
          sx={{ mt: 2 }}
        >
          Add Another Author
        </Button>
      )}
    </Paper>
  );

  const renderFileUploads = () => (
    <Paper sx={{ p: 4, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        File Uploads
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Alert severity="info" sx={{ mb: 3, py: 1, px: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="caption" fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
            📋 Requirements:
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Typography variant="caption" color="text.secondary">
              Paper: PDF/DOC (max 5MB)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              CV: PDF (max 2MB each)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Statement: PDF (max 1MB)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Anonymous Manuscript: PDF (required)
            </Typography>
            {category === "Mahasiswa" && (
              <Typography variant="caption" color="text.secondary">
                PDDIKTI: PDF (per student)
              </Typography>
            )}
            {category === "Umum" && (
              <Typography variant="caption" color="text.secondary">
                KTP: PDF/JPG/PNG (required)
              </Typography>
            )}
          </Box>
        </Box>
      </Alert>

      <Box sx={{ 
        display: "flex", 
        flexDirection: { xs: "column", sm: "row" }, 
        gap: 3,
        flexWrap: "wrap"
      }}>
        {/* Paper File */}
        <Box sx={{ 
          flex: { sm: "1 1 calc(50% - 12px)" }, 
          minWidth: { xs: "100%", sm: "280px" }
        }}>
          <Box
            sx={{
              border: 2,
              borderColor: "divider",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              "&:hover": { borderColor: "primary.main", bgcolor: "primary.lighter" },
              height: "100%",
            }}
          >
            <input
              accept=".pdf,.doc,.docx"
              style={{ display: "none" }}
              id="paper-file"
              type="file"
              onChange={(e) => handleFileUpload(e, "paper_file")}
            />
            <label htmlFor="paper-file">
              <CloudUpload sx={{ fontSize: 48, color: "action.active", mb: 1 }} />
              <Typography variant="body1" fontWeight={600}>
                Paper File
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {files.paper_file ? files.paper_file.name : "Click to upload"}
              </Typography>
              {fileErrors.paper_file && (
                <Typography variant="caption" color="error">
                  {fileErrors.paper_file}
                </Typography>
              )}
            </label>
          </Box>
        </Box>

        {/* Statement Letter */}
        <Box sx={{ 
          flex: { sm: "1 1 calc(50% - 12px)" }, 
          minWidth: { xs: "100%", sm: "280px" }
        }}>
          <Box
            sx={{
              border: 2,
              borderColor: "divider",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              "&:hover": { borderColor: "primary.main", bgcolor: "primary.lighter" },
              height: "100%",
            }}
          >
            <input
              accept=".pdf"
              style={{ display: "none" }}
              id="statement-file"
              type="file"
              onChange={(e) => handleFileUpload(e, "statement_file")}
            />
            <label htmlFor="statement-file">
              <CloudUpload sx={{ fontSize: 48, color: "action.active", mb: 1 }} />
              <Typography variant="body1" fontWeight={600}>
                Statement Letter
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {files.statement_file ? files.statement_file.name : "Click to upload"}
              </Typography>
              {fileErrors.statement_file && (
                <Typography variant="caption" color="error">
                  {fileErrors.statement_file}
                </Typography>
              )}
            </label>
          </Box>
        </Box>

        {/* Author Files */}
        {authors.map((author, index) => (
          <Box key={index} sx={{ 
            flex: { sm: "1 1 100%" }, 
            minWidth: { xs: "100%", sm: "280px" }
          }}>
            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
              Author {index + 1}: {author.name}
            </Typography>
            <Box sx={{ 
              display: "flex", 
              flexDirection: { xs: "column", sm: "row" },
              gap: 2, 
              mt: 2 
            }}>
              <Box
                sx={{
                  flex: 1,
                  border: 2,
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 2,
                  textAlign: "center",
                  cursor: "pointer",
                  "&:hover": { borderColor: "primary.main", bgcolor: "primary.lighter" },
                }}
              >
                <input
                  accept=".pdf"
                  style={{ display: "none" }}
                  id={`cv-file-${index}`}
                  type="file"
                  onChange={(e) => handleFileUpload(e, "cv_files", index)}
                />
                <label htmlFor={`cv-file-${index}`}>
                  <UploadFile sx={{ fontSize: 32, color: "action.active", mb: 1 }} />
                  <Typography variant="body2">CV File</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {files.cv_files[index] ? files.cv_files[index].name : "Upload CV"}
                  </Typography>
                </label>
              </Box>
              {category === "Mahasiswa" && (
                <Box
                  sx={{
                    flex: 1,
                    border: 2,
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                    textAlign: "center",
                    cursor: "pointer",
                    "&:hover": { borderColor: "primary.main", bgcolor: "primary.lighter" },
                  }}
                >
                  <input
                    accept=".pdf"
                    style={{ display: "none" }}
                    id={`pddikti-file-${index}`}
                    type="file"
                    onChange={(e) => handleFileUpload(e, "pddikti_files", index)}
                  />
                  <label htmlFor={`pddikti-file-${index}`}>
                    <UploadFile sx={{ fontSize: 32, color: "action.active", mb: 1 }} />
                    <Typography variant="body2">PDDIKTI Proof</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {files.pddikti_files[index] ? files.pddikti_files[index].name : "Upload PDDIKTI"}
                    </Typography>
                  </label>
                </Box>
              )}
              {category === "Umum" && (
                <Box
                  sx={{
                    flex: 1,
                    border: 2,
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                    textAlign: "center",
                    cursor: "pointer",
                    "&:hover": { borderColor: "primary.main", bgcolor: "primary.lighter" },
                  }}
                >
                  <input
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: "none" }}
                    id={`ktp-file-${index}`}
                    type="file"
                    onChange={(e) => handleFileUpload(e, "ktp_files", index)}
                  />
                  <label htmlFor={`ktp-file-${index}`}>
                    <UploadFile sx={{ fontSize: 32, color: "action.active", mb: 1 }} />
                    <Typography variant="body2">KTP</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {files.ktp_files[index] ? files.ktp_files[index].name : "Upload KTP"}
                    </Typography>
                  </label>
                </Box>
              )}
            </Box>
          </Box>
        ))}

        {/* Manuskrip Paper (Anonymous/Blind Version) */}
        <Box sx={{
          flex: { sm: "1 1 calc(50% - 12px)" },
          minWidth: { xs: "100%", sm: "280px" }
        }}>
          <Box
            sx={{
              border: 2,
              borderColor: "divider",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              "&:hover": { borderColor: "primary.main", bgcolor: "primary.lighter" },
              height: "100%",
            }}
          >
            <input
              accept=".pdf"
              style={{ display: "none" }}
              id="anonymous-manuscript-file"
              type="file"
              onChange={(e) => handleFileUpload(e, "anonymous_manuscript")}
            />
            <label htmlFor="anonymous-manuscript-file">
              <CloudUpload sx={{ fontSize: 48, color: "action.active", mb: 1 }} />
              <Typography variant="body1" fontWeight={600}>
                Manuskrip Paper (Anonymous)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {files.anonymous_manuscript ? files.anonymous_manuscript.name : "Click to upload"}
              </Typography>
              {fileErrors.anonymous_manuscript && (
                <Typography variant="caption" color="error">
                  {fileErrors.anonymous_manuscript}
                </Typography>
              )}
            </label>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block", textAlign: "center", fontStyle: "italic" }}>
            File paper tanpa mencantumkan identitas penulis (FORMAT PDF)
          </Typography>
        </Box>
      </Box>
    </Paper>
  );

  const renderReview = () => (
    <Paper sx={{ p: 4, borderRadius: 3 }}>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Review & Save Draft
      </Typography>
      <Divider sx={{ mb: 3 }} />

      <Alert severity="warning" sx={{ mb: 3 }} icon={<Warning />}>
        <AlertTitle>Please Review Carefully</AlertTitle>
        Make sure all information is correct before saving. You can still edit this paper later as long as it has not been submitted for review.
      </Alert>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Paper Title
        </Typography>
        <Typography variant="h6" fontWeight={600} sx={{ color: "text.primary", lineHeight: 1.4 }}>
          {title}
        </Typography>
      </Paper>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 3, mb: 3 }}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Info fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={600} color="primary">
              Paper Information
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="body2" color="text.secondary">Type</Typography>
              <Chip label={paperType} size="small" variant="soft" color="primary" />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="body2" color="text.secondary">Sub-Theme</Typography>
              <Typography variant="body2" fontWeight={500}>{subTheme}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="body2" color="text.secondary">Category</Typography>
              <Chip label={category === "Mahasiswa" ? "Student" : "General"} size="small" color={category === "Mahasiswa" ? "secondary" : "default"} />
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1 }}>
              <Typography variant="body2" color="text.secondary">Participation</Typography>
              <Chip label={participationType === "Individu" ? "Individual" : "Group"} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <Group fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={600} color="primary">
              Authors ({authors.length})
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {authors.map((author, index) => (
              <Box key={index} sx={{ p: 1.5, borderRadius: 1.5, bgcolor: "grey.50", border: 1, borderColor: "divider" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: "primary.main", fontSize: 14 }}>
                    {author.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {index === 0 ? "Primary Author" : `Author ${index + 1}`}: {author.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {author.email}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Uploaded Files
      </Typography>
      <Paper variant="outlined" sx={{ p: 0, borderRadius: 2, overflow: "hidden", mb: 3 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, bgcolor: "grey.50" }}>
          <Box sx={{ p: 2, borderRight: 1, borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Article fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight={600}>Paper File</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all", display: "block" }}>
              {files.paper_file?.name || "—"}
            </Typography>
          </Box>
          <Box sx={{ p: 2, borderRight: 1, borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Description fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight={600}>Anonymous</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all", display: "block" }}>
              {files.anonymous_manuscript?.name || "—"}
            </Typography>
          </Box>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Draw fontSize="small" color="primary" />
              <Typography variant="body2" fontWeight={600}>Statement</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all", display: "block" }}>
              {files.statement_file?.name || "—"}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Author Documents
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
        {authors.map((author, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600} color="primary" gutterBottom>
              {index === 0 ? "Primary Author" : `Author ${index + 1}`}: {author.name}
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, mt: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <UploadFile fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">CV</Typography>
                  <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                    {files.cv_files[index]?.name || "—"}
                  </Typography>
                </Box>
              </Box>
              {category === "Mahasiswa" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Description fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">PDDIKTI</Typography>
                    <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                      {files.pddikti_files[index]?.name || "—"}
                    </Typography>
                  </Box>
                </Box>
              )}
              {category === "Umum" && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Description fontSize="small" color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">KTP</Typography>
                    <Typography variant="caption" sx={{ wordBreak: "break-all" }}>
                      {files.ktp_files[index]?.name || "—"}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        ))}
      </Box>

      <FormControlLabel
        control={
          <Checkbox 
            checked={confirmAccuracy}
            onChange={(e) => setConfirmAccuracy(e.target.checked)}
          />
        }
        label={
          <Typography variant="body2">
            I confirm that all information provided is accurate and I agree to save this paper as draft
          </Typography>
        }
        sx={{ mt: 3 }}
      />
    </Paper>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>
        Create New Paper
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Follow the steps below to create and save your paper as draft
      </Typography>

      {loadingUserData ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
          <LinearProgress />
          <Typography sx={{ ml: 2 }}>Loading your profile...</Typography>
        </Box>
      ) : !userData ? (
        <Alert severity="error" sx={{ mt: 3 }}>
          Failed to load user data. Please <strong>refresh the page</strong> and try again.
        </Alert>
      ) : !subThemesLoaded ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
          <LinearProgress />
          <Typography sx={{ ml: 2 }}>Loading...</Typography>
        </Box>
      ) : !initialized ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
          <LinearProgress />
          <Typography sx={{ ml: 2 }}>Checking your submissions...</Typography>
        </Box>
      ) : hasActiveSubmission ? (
        <Alert severity="info" sx={{ mb: 3 }} icon={<Info />}>
          <AlertTitle>Submission Already Exists</AlertTitle>
          You have a submitted paper that is awaiting review. Therefore, you cannot create a new paper
          
          <br />
          Check your submission status in your <strong>Paper List</strong>.
        </Alert>
      ) : (
        <>
          {/* Horizontal Stepper */}
          <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Stepper activeStep={activeStep} orientation="horizontal">
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                StepIconProps={{
                  sx: {
                    '&.Mui-active': { color: 'primary.main' },
                    '&.Mui-completed': { color: 'primary.main' },
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {renderStepContent(activeStep)}

      <Box sx={{ mt: 3 }}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            disabled={activeStep === 0 || loading}
            onClick={handleBack}
            variant="outlined"
          >
            Back
          </Button>
          <Stack direction="row" spacing={2}>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !confirmAccuracy}
                startIcon={<CheckCircle />}
                size="large"
              >
                Save as Draft
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading}
                size="large"
              >
                Next
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
        </>
      )}

      {/* Remove Author Confirmation Dialog */}
      <ConfirmationDialog
        open={removeAuthorDialog.open}
        onClose={() => setRemoveAuthorDialog({ open: false, index: null, name: "" })}
        onConfirm={() => {
          setAuthors(authors.filter((_, i) => i !== removeAuthorDialog.index));
          setRemoveAuthorDialog({ open: false, index: null, name: "" });
        }}
        title="Remove Author"
        message={`Are you sure you want to remove "${removeAuthorDialog.name}" from the author list? This action cannot be undone.`}
        confirmText="Remove"
        cancelText="Cancel"
        variant="danger"
      />
    </Container>
  );
}
